/**
 * 투자 건 — **자금원 × 투자처 한 쌍**.
 *
 * ⚠️ 같은 회사에 자체 자금으로도, 위탁 자금으로도 넣으면 **건이 둘**이다.
 *    원장이 그래야 맞는다. 화면에서는 회사 기준으로 합쳐 보여준다.
 */
export interface Deal {
  id: number;
  vehicleId: number;
  companyId: number;
  stage: DealStage;
  /** 무엇을 받았나. 모르면 null */
  securityKind: SecurityKind | null;
  /** 신주 · 구주 (009) */
  acquisition?: Acquisition;
  /** 첫 납입일. 검토 단계면 null */
  investedOn: string | null;
  note: string;
  createdAt: string;
  // VICS 1319 · 1320 — DB v0.3
  shares?: number | null;
  pricePerShare?: number | null;
  contractOn?: string | null;
  paidOn?: string | null;
  registeredOn?: string | null;
  /** secondary 구주 매각 · ipo · mna · redemption 상환 */
  exitMethod?: 'secondary' | 'ipo' | 'mna' | 'redemption' | null;
}

export const DEAL_STAGES = ['검토', '투자', '회수', '상각'] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

export const STAGE_STYLE: Record<DealStage, string> = {
  '검토': 'bg-slate-100 text-slate-600',
  '투자': 'bg-indigo-50 text-indigo-600',
  '회수': 'bg-emerald-50 text-emerald-700',
  '상각': 'bg-red-50 text-red-600',
};

/** 인수 방식 — 구주는 회사가 아니라 기존 주주에게 돈이 간다. 투자의무 비율에 넣지 않는다 (009) */
export type Acquisition = 'new' | 'secondary';
export const ACQUISITION_LABEL: Record<Acquisition, string> = { new: '신주', secondary: '구주' };

export const SECURITY_KINDS = ['보통주', '우선주', 'RCPS', 'CB', 'SAFE'] as const;
export type SecurityKind = (typeof SECURITY_KINDS)[number];

export const SECURITY_NOTE: Record<SecurityKind, string> = {
  '보통주': '의결권 있는 일반 주식',
  '우선주': '배당 · 잔여재산 분배에 우선권이 있는 주식',
  'RCPS': '상환전환우선주 — 상환 청구와 보통주 전환이 가능한 우선주',
  'CB': '전환사채 — 정해진 조건으로 주식으로 바꿀 수 있는 사채',
  'SAFE': '조건부지분인수계약 — 다음 투자 때 주식으로 받는 계약',
};

/**
 * 평가액 — **덮어쓰지 않고 시점마다 쌓는다.**
 * 덮어쓰면 「작년에 얼마로 봤었나」를 못 본다. 지표에는 최신 한 줄만 쓴다.
 */
export interface Valuation {
  id: number;
  dealId: number;
  valuedOn: string;
  /** 이 건의 보유분 평가액 */
  ourValue: number;
  /** 그때 회사 전체 밸류 (참고) */
  companyValue: number | null;
  source: ValuationSource;
  note: string;
}

export const VALUATION_SOURCES = ['round', 'manual'] as const;
export type ValuationSource = (typeof VALUATION_SOURCES)[number];

export const SOURCE_LABEL: Record<ValuationSource, string> = {
  round: '라운드 파생',
  manual: '수기',
};

export interface DealForm {
  vehicleId: number | null;
  companyId: number | null;
  securityKind: SecurityKind | '';
  acquisition: Acquisition;
  investedOn: string;
  note: string;
  shares: number | null;
  pricePerShare: number | null;
}

export interface ValuationForm {
  valuedOn: string;
  ourValue: number | null;
  companyValue: number | null;
  source: ValuationSource;
  note: string;
}
